import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Camera, RefreshCw, CheckCircle2, AlertCircle, ArrowRight, X } from "lucide-react";
import api, { API_BASE } from "../lib/api";
import { formatPriceINR } from "../lib/format";
import SafeImage from "../components/SafeImage";

const AdminImportCarWalePhotos = () => {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [limit, setLimit] = useState(3);
  const [fullMode, setFullMode] = useState(false);
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState(null);
  const [summary, setSummary] = useState(null);
  const [expandedGallery, setExpandedGallery] = useState(null);

  const runPreview = async () => {
    setLoading(true); setPreview(null); setSummary(null);
    try {
      const body = fullMode ? { full: true } : { limit };
      const r = await api.post("/admin/import/carwale-photos/preview", body, { timeout: 300000 });
      setPreview(r.data);
      const defaults = new Set(r.data.matched.filter((m) => !m.would_replace_existing).map((m) => m.vehicle_id));
      setSelectedIds(defaults);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Preview failed");
    } finally { setLoading(false); }
  };

  useEffect(() => { runPreview(); /* eslint-disable-next-line */ }, []);

  const toggle = (id) => {
    const s = new Set(selectedIds);
    s.has(id) ? s.delete(id) : s.add(id);
    setSelectedIds(s);
  };
  const selectAll = () => setSelectedIds(new Set((preview?.matched || []).filter((m) => !m.would_replace_existing).map((m) => m.vehicle_id)));
  const deselectAll = () => setSelectedIds(new Set());

  const confirmImport = async () => {
    if (selectedIds.size === 0) { toast.error("Select at least one vehicle"); return; }
    setRunning(true); setSummary(null);
    setProgress({ message: "Starting import…", perVehicle: {} });
    try {
      const resp = await fetch(`${API_BASE}/admin/import/carwale-photos/confirm`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("autobahn_admin_token")}` },
        body: JSON.stringify({ vehicle_ids: Array.from(selectedIds), skip_if_has_existing: true }),
      });
      if (!resp.ok || !resp.body) throw new Error(`HTTP ${resp.status}`);
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      const perVehicle = {};
      let msg = "";
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const events = buffer.split("\n\n"); buffer = events.pop() || "";
        for (const ev of events) {
          const line = ev.trim(); if (!line.startsWith("data:")) continue;
          try {
            const p = JSON.parse(line.slice(5).trim());
            if (p.type === "vehicle_start") {
              perVehicle[p.vehicle_id] = { total: p.photo_total, done: 0, failed: 0, summary: p.summary };
              msg = `Importing vehicle ${p.v_index} of ${p.v_total} · ${p.summary}`;
            } else if (p.type === "photo_progress") {
              msg = `Importing vehicle ${p.v_index} of ${p.v_total} — downloading image ${p.p_index} of ${p.p_total}`;
            } else if (p.type === "vehicle_done" || p.type === "vehicle_skipped" || p.type === "vehicle_no_photos" || p.type === "vehicle_error") {
              const e = p.entry;
              perVehicle[e.vehicle_id] = { ...perVehicle[e.vehicle_id], done: e.downloaded, failed: (e.failed||[]).length, skipped: e.skipped_reason, summary: e.summary };
            } else if (p.type === "done") {
              setSummary(p.summary);
              msg = "Import complete.";
            }
            setProgress({ message: msg, perVehicle: { ...perVehicle } });
          } catch {}
        }
      }
      toast.success("Photo import complete");
    } catch (e) {
      toast.error("Import failed: " + e.message);
    } finally { setRunning(false); }
  };

  return (
    <div className="space-y-6" data-testid="admin-import-photos">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="overline text-[#7A1519]">Photo Import · CarWale</div>
          <h1 className="font-display text-3xl md:text-4xl tracking-tight font-semibold">CarWale Photo Import</h1>
          <p className="text-sm text-[#242424]/80 mt-2 max-w-2xl">Discover Autobahn's CarWale listings, match each one to its Autobahn vehicle via the embedded CarTrade ID, then download and store the photos in Autobahn's own storage.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <label className="text-sm inline-flex items-center gap-2 border hairline bg-white px-3 py-2.5 rounded-sm">
            <input type="checkbox" checked={fullMode} onChange={(e) => setFullMode(e.target.checked)} className="h-4 w-4 accent-[#7A1519]" data-testid="ph-full-mode" />
            Full inventory
          </label>
          {!fullMode && (
            <label className="text-sm text-[#242424]/75 inline-flex items-center gap-2">Limit
              <input type="number" min={1} max={20} value={limit} onChange={(e) => setLimit(Number(e.target.value)||3)} className="w-16 bg-white border hairline px-2 py-1 rounded-sm text-sm" data-testid="ph-limit" />
            </label>
          )}
          <button onClick={runPreview} disabled={loading || running} className="border hairline bg-white px-4 py-2.5 rounded-sm text-sm inline-flex items-center gap-2 disabled:opacity-60" data-testid="ph-refresh">
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> {loading ? "Scanning CarWale…" : "Refresh Preview"}
          </button>
        </div>
      </div>

      {preview && (
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3 text-sm">
          <Stat label="Listings scanned" value={preview.counts.listings_on_dealer_page} />
          <Stat label="Matched (eligible)" value={preview.counts.matched} highlight />
          <Stat label="Skipped (has images)" value={preview.counts.skipped_has_images ?? 0} />
          <Stat label="Unmatched" value={preview.counts.unmatched_listings} />
          <Stat label="Ambiguous" value={preview.counts.ambiguous} />
          <Stat label="Photos expected" value={preview.counts.total_photos_expected ?? 0} highlight />
        </div>
      )}

      {preview?.skipped_has_images?.length > 0 && (
        <div className="bg-white border hairline rounded-sm p-4" data-testid="ph-skipped">
          <div className="overline text-[#7A1519] mb-2">Skipped — already have Autobahn images ({preview.skipped_has_images.length})</div>
          <div className="text-sm text-[#242424]/80 mb-3">These vehicles are excluded automatically to protect your existing photos.</div>
          <div className="text-sm space-y-1">
            {preview.skipped_has_images.slice(0, 20).map((s) => (
              <div key={s.cartrade_id} className="flex items-center justify-between border-b hairline py-1.5">
                <span>{s.summary}</span>
                <span className="text-xs text-[#242424]/70 font-mono">CarTrade {s.cartrade_id} · {s.existing_photo_count} existing</span>
              </div>
            ))}
            {preview.skipped_has_images.length > 20 && (
              <div className="text-xs text-[#242424]/60 pt-2">+ {preview.skipped_has_images.length - 20} more</div>
            )}
          </div>
        </div>
      )}

      {preview?.matched?.length > 0 && (
        <div className="bg-white border hairline rounded-sm">
          <div className="p-4 border-b hairline flex items-center justify-between flex-wrap gap-2">
            <div className="text-sm">{selectedIds.size} of {preview.matched.length} selected</div>
            <div className="flex gap-2">
              <button onClick={selectAll} className="text-xs border hairline px-3 py-1.5 rounded-sm" data-testid="ph-select-all">Select all (safe)</button>
              <button onClick={deselectAll} className="text-xs border hairline px-3 py-1.5 rounded-sm" data-testid="ph-deselect-all">Deselect all</button>
            </div>
          </div>
          <div className="divide-y hairline">
            {preview.matched.map((m) => (
              <div key={m.vehicle_id} className="p-4" data-testid={`ph-match-${m.cartrade_id}`}>
                <div className="flex items-start gap-4">
                  <input type="checkbox" checked={selectedIds.has(m.vehicle_id)} onChange={() => toggle(m.vehicle_id)} className="mt-1 h-4 w-4 accent-[#7A1519]" data-testid={`ph-cb-${m.cartrade_id}`} />
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-baseline gap-3">
                      <div className="font-display font-semibold text-[#111111]">{m.summary}</div>
                      <div className="text-xs font-mono text-[#242424]/70">CarTrade {m.cartrade_id}</div>
                      <a href={m.carwale_listing_url} target="_blank" rel="noreferrer" className="text-xs text-[#7A1519] hover:underline">CarWale listing ↗</a>
                      <div className="text-sm text-[#242424]/85 ml-auto">{m.photos_found} photos found</div>
                    </div>
                    {m.would_replace_existing && (
                      <div className="mt-2 text-xs text-[#7A1519] inline-flex items-center gap-1 bg-[#F7F6F3] border hairline px-2 py-1 rounded-sm">
                        <AlertCircle size={12} /> Already has {m.existing_photo_count} Autobahn-hosted image(s) — will be SKIPPED automatically
                      </div>
                    )}
                    <div className="mt-3 grid grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-2">
                      {m.photos.slice(0, 8).map((p, i) => (
                        <div key={i} className="aspect-[4/3] bg-[#F7F6F3] border hairline rounded-sm overflow-hidden">
                          <img src={p.url} alt="" className="w-full h-full object-cover" loading="lazy" referrerPolicy="no-referrer" />
                        </div>
                      ))}
                      {m.photos.length > 8 && (
                        <button onClick={() => setExpandedGallery(m)} className="aspect-[4/3] bg-[#F7F6F3] border hairline rounded-sm text-xs text-[#7A1519] hover:bg-white" data-testid={`ph-more-${m.cartrade_id}`}>+{m.photos.length - 8} more</button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {progress && (
        <div className="bg-[#111111] text-white p-6 rounded-sm sticky bottom-4" data-testid="ph-progress">
          <div className="overline text-white/60">Import running</div>
          <div className="mt-1 font-display text-lg">{progress.message}</div>
          <div className="mt-3 space-y-1 text-sm text-white/80">
            {Object.entries(progress.perVehicle).map(([vid, v]) => (
              <div key={vid} className="flex items-center justify-between">
                <span className="truncate max-w-md">{v.summary}</span>
                <span className="font-mono text-xs">
                  {v.skipped ? <span className="text-white/50">skipped: {v.skipped}</span> : `${v.done||0}/${v.total||0}${v.failed ? ` · ${v.failed} failed` : ""}`}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {summary && (
        <div className="bg-white border-l-4 border-[#7A1519] border hairline p-6 rounded-sm" data-testid="ph-summary">
          <div className="overline text-[#7A1519] flex items-center gap-2"><CheckCircle2 size={14} /> Import complete</div>
          <div className="mt-2 font-display text-2xl">{summary.total_downloaded} photo{summary.total_downloaded === 1 ? "" : "s"} imported</div>
          <div className="mt-1 text-sm text-[#242424]/80">{summary.total_failed} failed · {(summary.total_bytes/1024/1024).toFixed(2)} MB stored</div>
          <div className="mt-2 divide-y hairline text-sm">
            {summary.vehicles.map((v) => (
              <div key={v.vehicle_id} className="py-2 flex items-center justify-between gap-2">
                <span>{v.summary}</span>
                <span className="text-xs text-[#242424]/70 font-mono">
                  {v.skipped_reason ? `skipped — ${v.skipped_reason}` : `${v.downloaded} downloaded${v.failed?.length ? ` · ${v.failed.length} failed` : ""}`}
                </span>
              </div>
            ))}
          </div>
          <div className="mt-4 flex gap-2">
            <Link to="/admin/inventory" className="bg-[#7A1519] text-white px-5 py-2.5 rounded-sm text-sm inline-flex items-center gap-2">Go to Inventory <ArrowRight size={14} /></Link>
          </div>
        </div>
      )}

      {preview?.matched?.length > 0 && !summary && (
        <div className="bg-[#111111] text-white p-6 rounded-sm flex items-center justify-between gap-4 sticky bottom-4">
          <div>
            <div className="overline text-white/60">Ready to import</div>
            <div className="mt-1 font-display text-lg">{selectedIds.size} vehicle{selectedIds.size === 1 ? "" : "s"} · photos will be downloaded and stored in Autobahn's own storage</div>
            <div className="text-xs text-white/60 mt-1">Vehicles with existing Autobahn images will be skipped automatically. Original CarWale URLs are not hotlinked.</div>
          </div>
          <button onClick={confirmImport} disabled={running || selectedIds.size === 0} className="bg-[#7A1519] text-white px-6 py-3 rounded-sm text-sm font-semibold disabled:opacity-50 inline-flex items-center gap-2" data-testid="ph-confirm">
            <Camera size={14} /> {running ? "Importing…" : "Confirm Photo Import"}
          </button>
        </div>
      )}

      {expandedGallery && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-6" onClick={() => setExpandedGallery(null)}>
          <div className="bg-white max-w-6xl w-full max-h-[85vh] overflow-y-auto rounded-sm" onClick={(e) => e.stopPropagation()}>
            <div className="p-4 flex items-center justify-between border-b hairline">
              <div className="font-display text-lg">{expandedGallery.summary} · {expandedGallery.photos.length} photos</div>
              <button onClick={() => setExpandedGallery(null)}><X size={18} /></button>
            </div>
            <div className="p-4 grid grid-cols-2 md:grid-cols-4 gap-3">
              {expandedGallery.photos.map((p, i) => (
                <div key={i} className="aspect-[4/3] bg-[#F7F6F3] overflow-hidden rounded-sm">
                  <img src={p.url} alt="" className="w-full h-full object-cover" referrerPolicy="no-referrer" />
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const Stat = ({ label, value, highlight }) => (
  <div className={`border hairline p-4 rounded-sm ${highlight ? "bg-[#7A1519] text-white border-[#7A1519]" : "bg-white"}`}>
    <div className={`overline ${highlight ? "text-white/70" : "text-[#242424]/60"}`}>{label}</div>
    <div className={`mt-1 font-display text-2xl font-semibold ${highlight ? "text-white" : "text-[#111111]"}`}>{value}</div>
  </div>
);

export default AdminImportCarWalePhotos;
